import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import AdminAdvisorShareHttpClient from "../../../core/infrastructure/clients/v2/AdminAdvisorShareHttpClient";
import { 
    IAdvisorActivity, 
    IActivityResponse, 
    IActivityRecordResponse, 
    IAdvisorShareLedgerResponse,
    AdvisorActivityInput,
    IAdvisorLedgerResponse,
    IRecordActivityResponse,
    IAdvisorActivityLedgerEntry,
    AdvisorActivityLedgerEntryStatus
} from "../../../types/database";

const client = new AdminAdvisorShareHttpClient();

interface ValidationError {
    validation: string;
    code: string;
    message: string;
    path: string[];
}

interface ApiError {
    message: string;
    errors?: ValidationError[];
}

type AdvisorSharesState = {
    fetched: boolean;
    activities: IAdvisorActivity[];
    availableActivities: IAdvisorActivity[];
    error: { message: any } | null;
    selectedActivity: IAdvisorActivity | null;
    selectedAdvisorLedger: IAdvisorActivityLedgerEntry[] | null;
    selectedAdvisorTotal: IAdvisorShareLedgerResponse | null;
}

type AdvisorSharesHook = AdvisorSharesState & {
    reloadActivities: () => Promise<void>;
    createActivity: (activity: AdvisorActivityInput) => Promise<{ success: boolean; data?: IAdvisorActivity; error?: ApiError }>;
    updateActivity: (activityId: number, activity: Partial<AdvisorActivityInput>) => Promise<{ success: boolean; data?: IAdvisorActivity; error?: ApiError }>;
    recordActivity: (payload: { key: string; advisorId: string; projectId: number; notes?: string; metadata?: Record<string, any>; status?: AdvisorActivityLedgerEntryStatus }) => Promise<{ data: IAdvisorActivityLedgerEntry | null; error: any }>;
    loadAdvisorLedger: (advisorId: string) => Promise<{ success: boolean; data?: IAdvisorLedgerResponse; error?: any }>;
    loadAdvisorTotalShares: (advisorId: string) => Promise<{ success: boolean; data?: IAdvisorShareLedgerResponse; error?: any }>;
    deleteActivityRecord: (recordId: number) => Promise<{ success: boolean; error?: any }>;
    updateLedgerEntryStatus: (ledgerEntryId: number, status: AdvisorActivityLedgerEntryStatus) => Promise<{ data: IAdvisorActivityLedgerEntry | null; error: any }>;
}

const initState: AdvisorSharesState = {
    fetched: false,
    activities: [],
    availableActivities: [],
    error: null,
    selectedActivity: null,
    selectedAdvisorLedger: null,
    selectedAdvisorTotal: null
}

export const useAdvisorShares = (projectId?: number): AdvisorSharesHook => {
    const { getAccessTokenSilently } = useAuth0();
    const [state, setState] = useState(initState);

    const reloadActivities = async () => {
        setState(prev => ({
            ...prev,
            fetched: false
        }));

        const accessToken = await getAccessTokenSilently();
        const { data, error } = await client.getActivities(accessToken, projectId);

        if (data) {
            const projectActivities = data.activities?.filter(a => a.project?.id === projectId && a.is_active);
            const systemActivities = data.activities?.filter(a => !a.project?.id && a.is_active);
            const projectActivityKeys = projectActivities?.map(a => a.key);
            const availableActivities = [ ...projectActivities, ...systemActivities?.filter(a => !projectActivityKeys?.includes(a.key))];
            setState(prev => ({
                ...prev,
                fetched: true,
                activities: data.activities ?? [],
                availableActivities: availableActivities ?? [],
                error: error
            }));
        } else {
            console.error(error);
            setState(prev => ({
                ...prev,
                fetched: true,
                error
            }));
        }
    };

    const createActivity = async (activity: AdvisorActivityInput) => {
        const accessToken = await getAccessTokenSilently();
        let result;

        try {
            if (projectId) {
                const { data, error } = await client.createProjectActivity(accessToken, projectId, activity);
                result = { data, error };
            } else {
                const { data, error } = await client.createSystemActivity(accessToken, activity);
                result = { data, error };
            }

            if (result.data) {
                await reloadActivities();
                return { success: true, data: result.data };
            } else {
                // Extract error from response if it exists
                const apiError = result.error?.response?.data || result.error;
                console.error('API Error:', apiError);
                return { 
                    success: false, 
                    error: apiError as ApiError 
                };
            }
        } catch (error) {
            // Extract error from response if it exists
            const apiError = (error as any)?.response?.data || error;
            console.error('API Error:', apiError);
            return { 
                success: false, 
                error: apiError as ApiError 
            };
        }
    };

    const updateActivity = async (activityId: number, activity: Partial<AdvisorActivityInput>) => {
        const accessToken = await getAccessTokenSilently();
        try {
            const { data, error } = await client.updateActivity(accessToken, activityId, activity);

            if (data) {
                await reloadActivities();
                return { success: true, data };
            } else {
                // Extract error from response if it exists
                const apiError = error?.response?.data || error;
                console.error('API Error:', apiError);
                return { 
                    success: false, 
                    error: apiError as ApiError 
                };
            }
        } catch (error) {
            // Extract error from response if it exists
            const apiError = (error as any)?.response?.data || error;
            console.error('API Error:', apiError);
            return { 
                success: false, 
                error: apiError as ApiError 
            };
        }
    };

    const recordActivityLedgerEntry = async (payload: { key: string; advisorId: string; projectId: number; notes?: string; metadata?: Record<string, any>; status?: AdvisorActivityLedgerEntryStatus }) => {
        const accessToken = await getAccessTokenSilently();
        return await client.recordActivityLedgerEntry(accessToken, payload);
    };

    const updateLedgerEntryStatus = async (ledgerEntryId: number, status: AdvisorActivityLedgerEntryStatus) => {
        const accessToken = await getAccessTokenSilently();
        return await client.updateLedgerEntryStatus(accessToken, ledgerEntryId, status);
    };

    const loadAdvisorLedger = async (advisorId: string) => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await client.getAdvisorLedger(accessToken, advisorId, projectId);

        if (data) {
            const ledgerData = (data as unknown) as IAdvisorLedgerResponse;
            setState(prev => ({
                ...prev,
                selectedAdvisorLedger: ledgerData.ledger
            }));
            return { success: true, data: ledgerData };
        } else {
            console.error(error);
            return { success: false, error };
        }
    };

    const loadAdvisorTotalShares = async (advisorId: string) => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await client.getAdvisorTotalShares(accessToken, advisorId, projectId);

        if (data) {
            setState(prev => ({
                ...prev,
                selectedAdvisorTotal: data
            }));
            return { success: true, data };
        } else {
            console.error(error);
            return { success: false, error };
        }
    };

    const deleteActivityRecord = async (recordId: number) => {
        const accessToken = await getAccessTokenSilently();
        return await client.deleteActivityRecord(accessToken, recordId);
    };

    useEffect(() => {
        let isMounted = true;

        const fetchActivities = async () => {
            if (!isMounted) return;
            await reloadActivities();
        };

        fetchActivities();

        return () => { isMounted = false };
    }, [projectId]);

    return {
        ...state,
        reloadActivities,
        createActivity,
        updateActivity,
        recordActivity: recordActivityLedgerEntry,
        loadAdvisorLedger,
        loadAdvisorTotalShares,
        deleteActivityRecord,
        updateLedgerEntryStatus
    };
}; 